import React, { forwardRef, memo, useCallback, useImperativeHandle } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
    Easing,
    SharedValue,
    cancelAnimation,
    runOnJS,
    runOnUI,
    useAnimatedStyle,
    withRepeat,
    withTiming,
} from 'react-native-reanimated';

import Shine from '@/assets/icons/shine';
import { Image } from '@/components/lib/components';
import { Box } from '@/components/lib/components/Box';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { designSystem, withOpacity } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { getTargetAngle } from '../../utils/mystery-wheel-utils';
import { BORDER_WIDTH, CENTER_LOGO_SIZE, SvgWheel, WHEEL_OFFSET_DEG } from './SvgWheel';

interface Props {
    multipliers: number[];
    size?: number;
    rotation: SharedValue<number>;
    onWheelStop?: () => void;
}
const LOOP_DURATION = 1500;
const WIND_BACK_DURATION = 500;
export const DEFAULT_WHEEL_SIZE = 546;

export interface MysteryWheelRef {
    startRotation: () => void;
    stopRotation: (target: number) => void;
    cancelRotation: () => void;
}
export const MysteryWheel = memo(
    forwardRef<MysteryWheelRef, Props>(
        ({ multipliers, size = DEFAULT_WHEEL_SIZE, rotation, onWheelStop }: Props, ref) => {
            const wheelDiameter = size - BORDER_WIDTH;
            const animatedStyle = useAnimatedStyle(() => {
                return {
                    transform: [{ rotate: `${rotation.value}deg` }],
                };
            });

            const stopRotation = useCallback(
                (targetDeg: number) => {
                    'worklet';
                    // current state
                    const current = rotation.value; // position x₀  (deg)
                    const v0 = 360 / LOOP_DURATION; // velocity v₀  (deg·ms⁻¹)
                    // remaining distance
                    const delta = (targetDeg - (current % 360) + 360) % 360; // Δ  = forward arc from current slot to target slot (0–359)
                    const distance = 720 + delta; // +1 extra full turn
                    // braking duration
                    // constant decel kinematics:  distance = ½ · v0 · T   (because v falls to 0)
                    // ⇒  T = 2·distance / v0
                    const duration = (2 * distance) / v0;
                    cancelAnimation(rotation);
                    rotation.value = withTiming(
                        current + distance + WHEEL_OFFSET_DEG, // end angle
                        {
                            duration,
                            // Quadratic ease‑out → position(t) = 1 − (1−t)²
                            // ‑ derivative at t=0 is 2, which matches the v0‑to‑0 profile
                            easing: (t: number) => {
                                'worklet';
                                return 1 - (1 - t) * (1 - t);
                            },
                        },
                        finished => {
                            if (finished) {
                                rotation.value = targetDeg + WHEEL_OFFSET_DEG; // final alignment
                                if (onWheelStop) {
                                    runOnJS(onWheelStop)();
                                }
                            }
                        }
                    );
                },
                [onWheelStop, rotation]
            );
            const startRotation = useCallback(() => {
                'worklet';
                cancelAnimation(rotation);
                rotation.value = withTiming(
                    // wind back 20 degrees
                    rotation.value - 20,
                    {
                        duration: WIND_BACK_DURATION,
                        easing: Easing.out(Easing.quad),
                    },
                    finished => {
                        if (finished) {
                            // continue the spin clockwise
                            rotation.value = withRepeat(
                                withTiming(rotation.value + 360, {
                                    duration: LOOP_DURATION,
                                    easing: Easing.linear,
                                }),
                                -1,
                                false
                            );
                        }
                    }
                );
            }, [rotation]);

            useImperativeHandle(ref, () => ({
                startRotation: () => {
                    runOnUI(startRotation)();
                },
                stopRotation: (target: number) => {
                    const targetAngle = getTargetAngle(multipliers, target);
                    runOnUI(stopRotation)(targetAngle);
                },
                cancelRotation: () => {
                    runOnUI(() => {
                        'worklet';
                        cancelAnimation(rotation);
                    })();
                },
            }));

            return (
                <Box justifyContent={'center'} alignItems={'center'}>
                    {/** animating the entire wheel because if we were to animate the svg components they would get re-drawn on each frame */}
                    {/** and by doing these some elements that must not rotate are rendered outside the wheel - ticker, gradient, center logo */}
                    <Animated.View style={animatedStyle}>
                        <SvgWheel size={size} numbers={multipliers} rotation={rotation} />
                    </Animated.View>
                    <Box
                        width={wheelDiameter - BORDER_WIDTH}
                        height={wheelDiameter - BORDER_WIDTH}
                        justifyContent={'flex-end'}
                        style={{
                            borderRadius: (wheelDiameter - BORDER_WIDTH) / 2,
                        }}
                        position={'absolute'}
                        overflow={'hidden'}
                    >
                        {isWeb ? (
                            <LinearGradient
                                colors={['transparent', withOpacity(designSystem.colors.neutralBlack, 0.4)]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 0, y: 1 }}
                                positions={[0.25, 1]}
                                style={StyleSheet.absoluteFill}
                            />
                        ) : (
                            <Shine width={540} height={405} />
                        )}
                    </Box>
                    {/** Rendering as pngs because the svgs have some styling that's not supported by react native */}
                    <Image source={require('@/assets/images/CenterRing.png')} style={styles.logo} />
                    <Image source={require('@/assets/images/Ticker.png')} style={styles.ticker} />
                </Box>
            );
        }
    )
);
const styles = StyleSheet.create({
    logo: {
        position: 'absolute',
        width: CENTER_LOGO_SIZE,
        height: CENTER_LOGO_SIZE,
    },
    ticker: {
        position: 'absolute',
        width: 49,
        height: 65,
        top: -12,
    },
});
